import React, { useState, useEffect } from 'react';
import InvestmentHeader1 from './investment-activity-report-header1';

const headerStyle = {
    backgroundColor: '#004080',
    color: '#fff',
    fontSize: '14px',
    fontFamily: 'Roboto Regular',
    fontWeight: 'normal',
    borderRight: '1px solid rgb(255 255 255 / 50%)',
    verticalAlign: 'middle',
    textAlign: 'center',
};

const tdStyle = {
    background: '#f5f5f5',
    borderBottom: '1px solid #ccc',
    borderRight: '1px solid rgb(255 255 255 / 50%)',
    padding: '5px 10px',
    color: '#333',
    fontSize: '12px',
};

const totalRowStyle = {
    background: '#fff',
    color: '#000',
    fontWeight: 'bold',
    textAlign: 'right',
    fontSize: '12px',
    padding: '10px',
};

const footerStyle = {
    borderTop: '2px solid #d5d5d5',
    textAlign: 'center',
    fontSize: '12px',
    padding: '10px 0',
    margin: 0,
};

const formatAmount = (amount) => {
    const formattedAmount = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(Number(amount)) || 0);
    return Number(amount) < 0 ? `(${formattedAmount})` : formattedAmount;
};

const InvestmentTable = ({ cashStatementDetails, clientAddressDetails, fromDate, toDate }) => {
    const [tableData, setTableData] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(12); // Adjust this if necessary

    useEffect(() => {
        const generateTableData = () => {
            const rows = [];

            cashStatementDetails.forEach((item) => {
                const { currency, details, totalDebit, totalCredit, totalBalance } = item;

                rows.push({ currency, isCurrencyRow: true });
                details.forEach((transaction) => {
                    rows.push(transaction);
                });

                rows.push({
                    tranDate: '',
                    valueDate: '',
                    comments: 'TOTAL',
                    debit: parseFloat(totalDebit).toFixed(2),
                    credit: parseFloat(totalCredit).toFixed(2),
                    balance: parseFloat(totalBalance).toFixed(2),
                });
            });

            setTableData(rows);
        };

        generateTableData();
    }, [cashStatementDetails]);

    const paginateTableData = (data, rowsPerPage) => {
        const paginatedData = [];
        for (let i = 0; i < data.length; i += rowsPerPage) {
            const page = data.slice(i, i + rowsPerPage);
            // Only add page if it has visible content
            if (page.length > 0 && page.some(row => row.comments || row.tranDate || row.debit || row.credit || row.balance)) {
                paginatedData.push(page);
            }
        }
        return paginatedData;
    };

    const paginatedTableData = paginateTableData(tableData, rowsPerPage);

    return (
        <div>
            {paginatedTableData.map((page, pageIndex) => {
                const hasContent = page.length > 0;
                return (
                    <div
                        key={pageIndex}
                        style={{
                            pageBreakAfter: pageIndex < paginatedTableData.length - 1 ? 'always' : 'auto',
                            pageBreakInside: 'avoid',
                            padding: '0',
                            margin: '0',
                        }}
                    >
                        <header>
                            <InvestmentHeader1 clientAddressDetails={clientAddressDetails} fromDate={fromDate}
                                toDate={toDate} />
                        </header>
                        <hr />
                        <main style={{ minHeight: '440px', pageBreakInside: 'avoid', padding: '0' }}>
                            <table
                                className="table"
                                style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    pageBreakInside: 'avoid',
                                }}
                            >
                                <thead>
                                    <tr style={headerStyle}>
                                        <th style={{ ...headerStyle, width: '10%' }}>Tran Date</th>
                                        <th style={{ ...headerStyle, width: '10%' }}>Value Date</th>
                                        <th style={{ ...headerStyle, width: '50%' }}>Comments</th>
                                        <th style={{ ...headerStyle, width: '10%' }}>Debit</th>
                                        <th style={{ ...headerStyle, width: '10%' }}>Credit</th>
                                        <th style={{ ...headerStyle, width: '10%' }}>Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {page.map((row, index) => (
                                        <React.Fragment key={index}>
                                            {row.isCurrencyRow ? (
                                                <tr style={{ backgroundColor: '#e7f3fe' }}>
                                                    <td colSpan={6} className="td-colors bg-white border-0 table-split-head moble-thead-change">
                                                        {row.currency}
                                                    </td>
                                                </tr>
                                            ) : row.comments === 'TOTAL' ? (
                                                <tr style={{ borderBottom: '1px solid #fff', background: '#d9edf7' }}>
                                                    <td style={totalRowStyle} colSpan={3}>Total</td>
                                                    <td style={{ ...totalRowStyle, borderTop: '1px solid #000', borderBottom: '1px solid #000' }}>
                                                        <span style={{ color: Number(row.debit) < 0 ? '#E43740' : 'black' }}>
                                                            {formatAmount(row.debit)}
                                                        </span>
                                                    </td>
                                                    <td style={{ ...totalRowStyle, borderTop: '1px solid #000', borderBottom: '1px solid #000' }}>
                                                        <span style={{ color: Number(row.credit) < 0 ? '#E43740' : 'black' }}>
                                                            {formatAmount(row.credit)}
                                                        </span>
                                                    </td>
                                                    <td style={{ ...totalRowStyle, borderTop: '1px solid #000', borderBottom: '1px solid #000' }}>
                                                        <span style={{ color: Number(row.balance) < 0 ? '#E43740' : 'black' }}>
                                                            {formatAmount(row.balance)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ) : (
                                                <tr style={{ backgroundColor: row.comments.includes('error') ? '#f8d7da' : '#fff' }}>
                                                    <td style={tdStyle}>{row.tranDate}</td>
                                                    <td style={tdStyle}>{row.valueDate}</td>
                                                    <td style={tdStyle}>{row.comments}</td>
                                                    <td style={{ ...tdStyle, textAlign: "right" }}>
                                                        <span style={{ color: Number(row.debit) < 0 ? '#E43740' : 'black' }}>
                                                            {formatAmount(row.debit)}
                                                        </span>
                                                    </td>
                                                    <td style={{ ...tdStyle, textAlign: "right" }}>
                                                        <span style={{ color: Number(row.credit) < 0 ? '#E43740' : 'black' }}>
                                                            {formatAmount(row.credit)}
                                                        </span>
                                                    </td>
                                                    <td style={{ ...tdStyle, textAlign: "right" }}>
                                                        <span style={{ color: Number(row.balance) < 0 ? '#E43740' : 'black' }}>
                                                            {formatAmount(row.balance)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </main>
                        {hasContent && (
                            <footer style={footerStyle}>
                                <div>A Subsidiary of DUBAI INVESTMENTS</div>
                                <div>
                                    {clientAddressDetails.building}, {clientAddressDetails.street}, {clientAddressDetails.citya}, {clientAddressDetails.countrya}.
                                    Office {clientAddressDetails.officeno}, P.O. Box-{clientAddressDetails.postbox}, Tel: {clientAddressDetails.mobile},
                                </div>
                                <div>Al Mal Capital PSC is Registered with Federal Tax Authority with VAT Number {clientAddressDetails.trnno}</div>
                                <div>Al Mal Capital PSC is incorporated in Dubai UAE under CR 76761 as a Private Joint Stock Company with a paid-up share capital of AED 219.78 Million</div>
                                <div>(Al Mal Capital PSC is licensed and regulated by the Securities and Commodities Authorities, License No# 603022)</div>
                            </footer>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default InvestmentTable;
